/**
 * What the running browser can and cannot do with media.
 *
 * The app is built against Chromium and there is a desktop shell planned, so
 * this is not a compatibility layer — it exists to keep the interface honest.
 * Telling somebody on Firefox to tick a checkbox that their browser never
 * shows sends them hunting for a setting that does not exist, which is worse
 * than saying plainly that it cannot be done here.
 */

/**
 * Whether the browser can capture the sound of a shared screen.
 *
 * Firefox has never implemented audio capture in `getDisplayMedia`: the
 * constraint is accepted and silently ignored, so the share arrives with video
 * and no audio track and nothing in the API says why. There is no feature test
 * for it — the call succeeds either way — which is why this reads the user
 * agent, the one place the difference is actually visible beforehand.
 */
export function canCaptureScreenAudio(userAgent: string = navigator.userAgent): boolean {
  return !isFirefox(userAgent);
}

export function isFirefox(userAgent: string = navigator.userAgent): boolean {
  return /firefox|fxios/i.test(userAgent);
}

/**
 * Whether audio output can be pointed at a chosen device.
 *
 * `setSinkId` is what moves playback to a specific speaker or headset. Where
 * it is missing the picker would list devices and change nothing, so the
 * control is hidden instead of lying.
 */
export function canChooseAudioOutput(): boolean {
  return typeof HTMLMediaElement !== 'undefined' && 'setSinkId' in HTMLMediaElement.prototype;
}

export interface ScreenAudioAdvice {
  /** Whether to say anything at all. */
  show: boolean;
  /** What to say, already in the user's language. */
  message: string;
}

/**
 * The right sentence for a share that arrived without sound.
 *
 * Two different problems wear the same symptom, and the fix for one is useless
 * for the other: on Chromium the checkbox was missed and can be ticked on the
 * next try, while on Firefox there is nothing to tick and the only way out is
 * another browser.
 */
export function screenAudioAdvice(options: {
  wanted: boolean;
  sharing: boolean;
  hasAudioTrack: boolean;
  userAgent?: string;
  /** Whether the desktop shell is what is running the page. */
  desktop?: boolean;
}): ScreenAudioAdvice {
  const { wanted, sharing, hasAudioTrack, userAgent, desktop = false } = options;

  if (!wanted || !sharing || hasAudioTrack) {
    return { show: false, message: '' };
  }

  // In the shell there is no browser checkbox to miss: the picker belongs to
  // the app itself, and the sound is a tick inside it.
  if (desktop) {
    return {
      show: true,
      message:
        'Você está compartilhando sem som. Pare e compartilhe de novo, marcando "Levar o som do ' +
        'sistema" no seletor do Discordo.',
    };
  }

  if (isFirefox(userAgent)) {
    return {
      show: true,
      message:
        'O Firefox não consegue enviar o som da tela — a limitação é do navegador, não há ajuste ' +
        'que resolva. Para compartilhar com som, use Chrome, Edge ou outro navegador baseado em ' +
        'Chromium.',
    };
  }

  return {
    show: true,
    message:
      'Você está compartilhando sem som. O navegador só envia o áudio se a caixa "compartilhar ' +
      'áudio" for marcada no seletor. Pare e compartilhe de novo, marcando a caixa — e escolha ' +
      'uma guia ou a tela inteira, porque para uma janela isolada não há opção de áudio.',
  };
}
